import { useState } from 'react';
import type { ExecutiveRefreshResponse, MeResponse } from '../../shared/api-types.js';
import { api, type ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { executiveOffered } from '../shell/executive-badge.js';
import { useMe } from '../shell/me-context.js';
import { Button, FailureView, SectionStrip, StateView, failureDetail, useNow, useToast, type ButtonVariant, type SectionStripItem } from '../ui/index.js';
import { EXECUTIVE_AUTHOR_LINE, EXECUTIVE_OFF_DESCRIPTION, EXECUTIVE_OFF_TITLE, REFRESH_EXPLAINER, REFRESH_QUEUED, refreshAgainSentence, refreshAnswerSentence } from './lib/executive.js';

// The parts every section of the executive module shares (ADR 0019; design §2.13): the section strip, the author line,
// the refusal states and "Refresh now". Kept apart from ExecutivePage.tsx, which imports the sections, so no section
// imports the page that renders it.

export type ExecutiveSectionId = 'report' | 'pulse' | 'matters' | 'coverage';

/** The sections this reader holds, in order; none for a reader the layer is not offered to (never enumerated, 02-patterns §2.4). */
export function executiveSectionsFor(me: Pick<MeResponse, 'capabilities' | 'integrations'>): SectionStripItem[] {
  if (!executiveOffered(me)) return [];
  return [
    { label: 'Report', to: PATHS.executive, end: true },
    { label: 'Pulse', to: PATHS.executivePulse },
    { label: 'Matters', to: PATHS.executiveMatters },
    { label: 'Coverage', to: PATHS.executiveCoverage },
  ];
}

/** The module's section strip (02-patterns §2.4), drawn on every route of the module. */
export function ExecutiveStrip() {
  const me = useMe();
  return <SectionStrip label="Executive sections" items={executiveSectionsFor(me)} />;
}

/** The author line (design §2.13 and §2.14 item 8), at the foot of every section. */
export function AuthorLine() {
  return <p className="executive-author" data-executive-author="">{EXECUTIVE_AUTHOR_LINE}</p>;
}

/**
 * A section's refusal or failure, as the state it is (03-components §1; app-baseline 05-client §5.4): not configured —
 * the floor off, or no report writer on this installation — says executive intelligence is off here; a refusal is the
 * permission-denied state with its next step; anything else says what failed and offers a retry.
 */
export function ExecutiveFailure({ failure, what, onRetry, backTo }: { failure: ApiFailure; what: string; onRetry?: () => void; backTo?: { to: string; label: string } }) {
  if (failure.code === 'feature_not_configured') return <StateView kind="not-configured" title={EXECUTIVE_OFF_TITLE} description={EXECUTIVE_OFF_DESCRIPTION} minHeight="16rem" />;
  return <FailureView failure={failure} what={what} onRetry={onRetry} backTo={backTo} minHeight="16rem" />;
}

/**
 * What "Refresh now" does, and when it may next be pressed — beside the button on the Report section and on Coverage alike,
 * so the reason a pressed-too-soon button gives is on the page as words too (03-components §2: a disabled control is never
 * the only explanation). Why on Coverage too (the owner's ruling of 2026-10-09 on ADR 0019's Open 39, "Coverage knows the cooldown"): its route
 * carries `cooldownUntil` now, the scope's cooldown and day's cap that the report's route carries.
 */
export function RefreshNote({ cooldownUntil, now }: { cooldownUntil: string | null; now: Date }) {
  return (
    <p className="executive-refresh-note text-sm secondary">
      {REFRESH_EXPLAINER}
      {cooldownUntil && Date.parse(cooldownUntil) > now.getTime() ? ` ${refreshAgainSentence(cooldownUntil, now)}` : ''}
    </p>
  );
}

export interface RefreshNowProps {
  /** When "Refresh now" may next be pressed (the scope's cooldown and day's cap); null when now. */
  cooldownUntil: string | null;
  /** A report already being written: a second press would start nothing (06-modules §6.5). */
  running: boolean;
  variant?: ButtonVariant;
  /** The server's answer: queued, or the first report's stated wait. */
  onAnswered: (answer: ExecutiveRefreshResponse) => void;
}

/**
 * "Refresh now" (the owner's pick E8): a report of what came in since the latest one, at the head of the queue — never a
 * replacement of it ("Non-overlapping windows", which superseded R3). Disabled, with the reason in words, while a report is
 * being written or the cooldown or the day's cap runs — on the Report section and, since its route carries `cooldownUntil`
 * (Open 39), on Coverage alike; the first report's wait is a stated, not-yet answer, never an error; a refusal the page
 * could not know in advance — another executive's press a moment before this one, the cap reached meanwhile — is said in the
 * server's own words.
 */
export function RefreshNow({ cooldownUntil, running, variant = 'primary', onAnswered }: RefreshNowProps) {
  const toast = useToast();
  const now = useNow(30_000);
  const [busy, setBusy] = useState(false);
  const cooling = typeof cooldownUntil === 'string' && Date.parse(cooldownUntil) > now.getTime();
  const disabledReason = running ? 'A report is being written now.' : cooling ? refreshAgainSentence(cooldownUntil, now) : undefined;
  const press = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const result = await api.post<ExecutiveRefreshResponse>('/api/executive/report/refresh');
    setBusy(false);
    if (!result.ok) {
      // Why a warning for these two: the cooldown, the day's cap and a report already being written are the server
      // declining a second report for now, each said in its own whole sentence — not a fault.
      if (result.code === 'rate_limited' || result.status === 409) toast.warning(result.message);
      else toast.error(<>We couldn’t start a refresh. {failureDetail(result)}</>);
      return;
    }
    if (result.data.state === 'queued') toast.success(REFRESH_QUEUED);
    else toast.info(refreshAnswerSentence(result.data, now));
    onAnswered(result.data);
  };
  return (
    <Button variant={variant} icon="refresh" onClick={() => void press()} loading={busy} disabled={Boolean(disabledReason)} disabledReason={disabledReason}>
      Refresh now
    </Button>
  );
}
